import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Icon } from "../ui/icons";

// Surfaces the structurer Claude pass's proposed attendees for a meeting.
// Promoted from inline-in-MeetingReview to its own file; rendered today
// inside the ReviewRail (studio layout).
//
// Task 6 (2026-05-14) added the per-row "Promote" affordance: click ->
// INSERT into the workspace's `attendees` registry. Already-promoted
// rows show "In registry" instead of the button. Dedup happens at the
// DB layer via the partial unique index from migration 0012; the SPA
// also checks the registry on mount to render the right initial state.
//
// Fix 3 (2026-06-12): explicit contained editing — click-on-text removed;
// pencil IconButton per row opens a stacked-inputs edit panel constrained
// to the rail width. Save/Cancel are explicit actions below the inputs.

export interface ProposedAttendee {
  display_name: string;
  role?: string;
  notes?: string;
}

interface EditDraft {
  display_name: string;
  role: string;
  notes: string;
}

interface Props {
  attendees: ProposedAttendee[];
  // Workspace id is required for the promote-to-registry write.
  workspaceId?: string;
  // Meeting's current project assignment. When non-null, Promote writes
  // the attendee into THIS project. When null (unassigned), Promote is
  // disabled with a plain-language hint.
  meetingProjectId?: string | null;
  // Curation: when editable, each row gets a pencil button that opens
  // an explicit edit panel (stacked inputs, Save/Cancel). Omit/false →
  // read-only (e.g. a locked meeting).
  editable?: boolean;
  onEdit?: (index: number, patch: Partial<ProposedAttendee>) => void;
  onRemove?: (index: number) => void;
  /** Called after a dismiss or registry-removal so the parent can surface a
   *  toast. `undoFn` is provided only for dismiss (not for remove-from-registry). */
  onToast?: (message: string, undoFn?: () => void) => void;
  /** Called by the undo path to re-insert a previously dismissed attendee. */
  onReinsert?: (attendee: ProposedAttendee) => void;
}

export function ProposedAttendeesCard({
  attendees,
  workspaceId,
  meetingProjectId,
  editable,
  onEdit,
  onRemove,
  onToast,
  onReinsert,
}: Props) {
  // Which row (by index) is in edit mode, and its working draft.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<EditDraft>({ display_name: "", role: "", notes: "" });

  // Cancel edit if the attendees array changes structurally (e.g. a Remove
  // elsewhere shifted indices).
  useEffect(() => {
    setEditingIndex(null);
  }, [attendees]);

  function openEdit(index: number) {
    const a = attendees[index];
    setDraft({
      display_name: a.display_name,
      role: a.role ?? "",
      notes: a.notes ?? "",
    });
    setEditingIndex(index);
  }

  function cancelEdit() {
    setEditingIndex(null);
  }

  function commitEdit(index: number) {
    const a = attendees[index];
    const name = draft.display_name.trim();
    if (!name) return; // display_name is required
    const patch: Partial<ProposedAttendee> = {};
    if (name !== (a.display_name || "").trim()) patch.display_name = name;
    const role = draft.role.trim();
    if (role !== (a.role ?? "").trim()) patch.role = role;
    const notes = draft.notes.trim();
    if (notes !== (a.notes ?? "").trim()) patch.notes = notes;
    if (Object.keys(patch).length > 0) onEdit?.(index, patch);
    setEditingIndex(null);
  }

  // ---------------------------------------------------------------------------
  // Registry state
  // ---------------------------------------------------------------------------

  const [registered, setRegistered] = useState<Set<string>>(new Set());
  const [registeredIds, setRegisteredIds] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function loadRegistered(ws: string) {
    setLoading(true);
    const { data } = await supabase
      .from("attendees")
      .select("id, display_name, project_id")
      .eq("workspace_id", ws)
      .is("deleted_at", null);
    const set = new Set<string>();
    const ids = new Map<string, string>();
    for (const row of data ?? []) {
      if (!meetingProjectId || row.project_id !== meetingProjectId) continue;
      const name = (row.display_name || "").trim().toLowerCase();
      if (name) {
        set.add(name);
        if (row.id) ids.set(name, row.id);
      }
    }
    setRegistered(set);
    setRegisteredIds(ids);
    setLoading(false);
  }

  useEffect(() => {
    if (!workspaceId) return;
    loadRegistered(workspaceId);
  }, [workspaceId, meetingProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function promote(a: ProposedAttendee) {
    if (!workspaceId) return;
    const key = a.display_name.trim().toLowerCase();
    if (!key || pending.has(key)) return;
    setPending((p) => new Set(p).add(key));
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
    const { error } = await supabase.from("attendees").insert({
      workspace_id: workspaceId,
      project_id: meetingProjectId ?? null,
      display_name: a.display_name.trim(),
      role: a.role?.trim() || null,
      notes: a.notes?.trim() || null,
      aliases: [],
    });
    setPending((p) => {
      const next = new Set(p);
      next.delete(key);
      return next;
    });
    if (error) {
      if (error.code === "23505") {
        setRegistered((r) => new Set(r).add(key));
      } else {
        setErrors((e) => ({ ...e, [key]: error.message }));
      }
      return;
    }
    setRegistered((r) => new Set(r).add(key));
    await loadRegistered(workspaceId);
  }

  async function removeFromRegistry(a: ProposedAttendee) {
    if (!workspaceId || !meetingProjectId) return;
    const key = a.display_name.trim().toLowerCase();
    const rowId = registeredIds.get(key);
    const confirmed = window.confirm(
      `Remove "${a.display_name}" from this project's registry permanently? It won't be re-suggested.`,
    );
    if (!confirmed) return;

    let deleteError: { message: string } | null = null;
    if (rowId) {
      const { error } = await supabase.from("attendees").delete().eq("id", rowId);
      deleteError = error;
    } else {
      const { error } = await supabase
        .from("attendees")
        .delete()
        .eq("project_id", meetingProjectId)
        .eq("display_name", a.display_name.trim());
      deleteError = error;
    }

    if (deleteError) {
      onToast?.("Remove failed — please try again");
      return;
    }

    const { addIgnore } = await import("../lib/registryIgnores");
    await addIgnore(workspaceId, meetingProjectId, "attendee", a.display_name.trim());

    setRegistered((r) => { const next = new Set(r); next.delete(key); return next; });
    setRegisteredIds((m) => { const next = new Map(m); next.delete(key); return next; });

    onToast?.("Removed from registry");
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 14, color: "var(--text-muted)" }}>
        Proposed attendees ({attendees.length})
      </h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
        Names extracted from your context notes and the meeting title.{" "}
        {meetingProjectId
          ? "Use Promote to add to this project's registry."
          : "Assign this meeting to a project to save names to a registry."}
      </p>

      {attendees.map((a, i) => {
        const key = a.display_name.trim().toLowerCase();
        const inRegistry = registered.has(key);
        const isPending = pending.has(key);
        const rowError = errors[key];
        const isEditing = editingIndex === i;

        return (
          <div
            key={i}
            className="proposed-row"
            style={{ borderBottom: "1px solid var(--border)", padding: "8px 0" }}
          >
            {isEditing ? (
              /* ---- Edit mode: stacked inputs, full width, contained ---- */
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 0 }}>
                  Name
                  <input
                    autoFocus
                    type="text"
                    dir="auto"
                    value={draft.display_name}
                    onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(i); }
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      marginTop: 2,
                      padding: "5px 8px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--accent)",
                      borderRadius: 4,
                      fontSize: 13,
                      fontFamily: "inherit",
                      fontWeight: 500,
                    }}
                  />
                </label>
                <label style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 0 }}>
                  Role
                  <input
                    type="text"
                    dir="auto"
                    value={draft.role}
                    onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(i); }
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      marginTop: 2,
                      padding: "5px 8px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: "monospace",
                    }}
                    placeholder="role"
                  />
                </label>
                <label style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 0 }}>
                  Notes
                  <input
                    type="text"
                    dir="auto"
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                      if (e.key === "Enter") { e.preventDefault(); commitEdit(i); }
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      boxSizing: "border-box",
                      marginTop: 2,
                      padding: "5px 8px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: "inherit",
                    }}
                    placeholder="notes"
                  />
                </label>
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => commitEdit(i)}
                    style={{ padding: "4px 12px", fontSize: 12, flex: "none" }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={cancelEdit}
                    style={{ padding: "4px 10px", fontSize: 12, flex: "none", color: "var(--text-muted)" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              /* ---- View mode ---- */
              <div>
                {/* Name row: text + pencil edit button + dismiss ✕ */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 4, marginBottom: 2 }}>
                  <span
                    dir="auto"
                    style={{ fontWeight: 500, fontSize: 13, flex: 1, minWidth: 0, wordBreak: "break-word" }}
                  >
                    {a.display_name}
                  </span>
                  {editable && (
                    <button
                      type="button"
                      onClick={() => openEdit(i)}
                      aria-label={`Edit ${a.display_name}`}
                      title="Edit"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-muted)",
                        opacity: 0.45,
                        padding: "0 2px",
                        fontSize: 12,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                      className="proposed-row-pencil"
                    >
                      <Icon name="edit" size={12} aria-hidden />
                    </button>
                  )}
                  {editable && (
                    <button
                      type="button"
                      className="row-remove"
                      onClick={() => {
                        const dismissed = a;
                        onRemove?.(i);
                        onToast?.("Suggestion dismissed", () => onReinsert?.(dismissed));
                      }}
                      aria-label={`Dismiss suggestion for ${a.display_name}`}
                      title="Dismiss suggestion"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-muted, #9ca3af)",
                        fontSize: 13,
                        padding: "0 2px",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Role */}
                {a.role && (
                  <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "monospace", marginBottom: 2 }}>
                    {a.role}
                  </div>
                )}

                {/* Notes */}
                {a.notes && (
                  <div dir="auto" style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4, wordBreak: "break-word" }}>
                    {a.notes}
                  </div>
                )}

                {/* Promote / In registry / hint */}
                {!workspaceId || loading ? null : inRegistry ? (
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>✓ In registry</span>
                    {editable && meetingProjectId && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => void removeFromRegistry(a)}
                        style={{ padding: "2px 8px", fontSize: 11, color: "var(--error, #f87171)", borderColor: "var(--error, #f87171)" }}
                        title={`Remove "${a.display_name}" from this project's registry`}
                      >
                        Remove from registry
                      </button>
                    )}
                  </div>
                ) : !meetingProjectId ? (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic", marginTop: 4 }}>
                    Assign to a project first
                  </div>
                ) : (
                  <div style={{ marginTop: 4 }}>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => promote(a)}
                      disabled={isPending}
                      style={{ padding: "2px 8px", fontSize: 11 }}
                      title="Add this name to this project's registry so future meetings in this project recognize them"
                    >
                      {isPending ? "Adding…" : "Promote"}
                    </button>
                  </div>
                )}

                {rowError && (
                  <div className="error" style={{ fontSize: 11, marginTop: 2 }}>{rowError}</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
